import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {ActionIcon, Box, Skeleton, Stack, Tooltip} from "@mantine/core";
import {useLocation} from "react-router-dom";
import {useMediaQuery} from "@mantine/hooks";
import {t} from "i18next";
import IconArrowLeft from "../../assets/arrow-left.svg?react";
import IconArrowRight from "../../assets/arrow-right.svg?react";
import IconArrowBarToUp from "../../assets/arrow-bar-to-up.svg?react";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {useReportAccess} from "./AccessContext";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import {TableOfContents} from "./TableOfContents";
import {TocTickRail} from "./TocTickRail";
import type {DibDocument} from "./types";
import {buildTocTree, flattenTocEntries, type TocEntry} from "./tocUtils";
import {findAnchorTarget} from "./anchorTarget";
import classes from "./HtmlArticle.module.css";
import layoutClasses from "./ContentLayout.module.css";
import embeddedClasses from "./EmbeddedTopic.module.css";
import {RelatedContent} from "./RelatedContent";
import {SupplierInfo} from "./SupplierInfo";
import {renderNode} from "./renderNode";
import {useContentTitle} from "./useContentTitle";

/**
 * Renders descroot entries — the document's description/abstract.
 * Each descroot entry is a container element whose children are the
 * actual description content (typically <p> elements).
 */
const renderDescriptionContent = (doc: DibDocument) =>
    doc.descroot.flatMap((entry) => {
        const el = doc.elements[entry.id];
        if (!el?.children) return [];
        return el.children
            .filter((c): c is {id: string} => "id" in c)
            .map((c) => renderNode(doc, c.id, {}, c.id));
    });

const renderMainContent = (doc: DibDocument) => {
    return doc.root.map((entry) => renderNode(doc, entry.id, {}, entry.id));
};

const DocumentSkeleton = () => (
    <Stack gap="sm" pt="xl">
        <Skeleton height={32} width="60%" mb="md" />
        <Skeleton height={14} width="100%" />
        <Skeleton height={14} width="95%" />
        <Skeleton height={14} width="88%" />
        <Skeleton height={14} width="92%" />
        <Skeleton height={14} width="40%" />
        <Skeleton height={24} width="45%" mt="xl" mb="xs" />
        <Skeleton height={14} width="100%" />
        <Skeleton height={14} width="97%" />
        <Skeleton height={14} width="85%" />
        <Skeleton height={14} width="60%" />
    </Stack>
);

export interface EmbeddedTopicOptions {
    compactToc: boolean;
    sectionId?: string;
    onNavigate?: (location: {segmentId?: string; sectionId?: string}) => void;
}

export const HTMLResource = ({
    resourceId,
    segmentId,
    embedded,
}: {
    resourceId: string;
    segmentId: string | undefined;
    embedded?: EmbeddedTopicOptions;
}) => {
    const [embeddedLocation, setEmbeddedLocation] = useState({
        resourceId,
        sourceSegmentId: segmentId,
        activeSegmentId: segmentId,
    });
    const isEmbedded = embedded !== undefined;
    const embeddedPropsChanged =
        embeddedLocation.resourceId !== resourceId ||
        embeddedLocation.sourceSegmentId !== segmentId;
    const activeSegmentId = isEmbedded
        ? embeddedPropsChanged
            ? segmentId
            : embeddedLocation.activeSegmentId
        : segmentId;
    const pendingSectionRef = useRef<string | undefined>(embedded?.sectionId);
    const onEmbeddedNavigate = embedded?.onNavigate;

    useEffect(() => {
        setEmbeddedLocation({
            resourceId,
            sourceSegmentId: segmentId,
            activeSegmentId: segmentId,
        });
        pendingSectionRef.current = embedded?.sectionId;
    }, [resourceId, segmentId, embedded?.sectionId]);

    const {
        currentData: documentData,
        isFetching: isDocumentFetching,
        isError,
        error,
    } = dibAppApi.endpoints.getDocument.useQuery({resourceId, segmentId: activeSegmentId});

    useReportAccess(resourceId, documentData?.access);

    // TOC document continuity — retain previous doc during segment transitions
    const prevDocRef = useRef<DibDocument | null>(null);
    if (documentData) {
        prevDocRef.current = documentData;
    }

    const isIntraTopicFetch =
        !documentData &&
        isDocumentFetching &&
        prevDocRef.current?.topicId === resourceId;
    const tocDocument =
        documentData ?? (isIntraTopicFetch ? prevDocRef.current : null);

    // Page title from the retained doc so same-topic segment fetches (which
    // blank currentData) don't flicker the title back to "KAI".
    useContentTitle(tocDocument?.name, !isEmbedded);

    const tocEntries = useMemo(() => {
        return tocDocument ? buildTocTree(tocDocument) : [];
    }, [tocDocument]);

    const flatTocEntries = useMemo(
        () => flattenTocEntries(tocEntries),
        [tocEntries]
    );

    const showToc =
        (!isDocumentFetching || isIntraTopicFetch) && tocEntries.length > 0;

    // Scroll restoration
    const location = useLocation();
    const articleRef = useRef<HTMLElement>(null);

    // Click-override: track last-clicked TOC item to override :target-current
    const [clickedTocId, setClickedTocId] = useState<string | null>(null);

    // Embedded scroll-spy: the embedded article scrolls inside its own box, so
    // the CSS :target-current mechanism cannot track it. Watch heading
    // elements and report the topmost one in view as the current heading.
    const [spyCurrentId, setSpyCurrentId] = useState<string | null>(null);

    useEffect(() => {
        if (!isEmbedded) return;
        const article = articleRef.current;
        if (!article || flatTocEntries.length === 0) return;

        const headings = flatTocEntries
            .map((entry) =>
                article.querySelector<HTMLElement>(`#${CSS.escape(entry.id)}`)
            )
            .filter((el): el is HTMLElement => el !== null);
        if (headings.length === 0) return;

        const visibleIds = new Set<string>();
        const observer = new IntersectionObserver(
            (observedEntries) => {
                for (const observedEntry of observedEntries) {
                    const id = observedEntry.target.id;
                    if (observedEntry.isIntersecting) visibleIds.add(id);
                    else visibleIds.delete(id);
                }
                const topmost = flatTocEntries.find((entry) =>
                    visibleIds.has(entry.id)
                );
                if (topmost) setSpyCurrentId(topmost.id);
            },
            {root: article, rootMargin: "0px 0px -70% 0px", threshold: 0}
        );

        headings.forEach((heading) => observer.observe(heading));
        return () => observer.disconnect();
    }, [isEmbedded, flatTocEntries, tocDocument]);

    // Click and scroll-spy agree on one "current heading" value. A click
    // stays as the current heading until the reader scrolls again and clears it.
    const embeddedCurrentId = clickedTocId ?? spyCurrentId ?? undefined;

    // Track completed page navigation so scrolling runs after the new content mounts.
    const prevParamsRef = useRef<{
        resourceId: string | undefined | null;
        segmentId: string | undefined | null;
    }>({resourceId: null, segmentId: null});

    const scrollArticleTo = useCallback(
        (target: HTMLElement, behavior: ScrollBehavior) => {
            const article = articleRef.current;
            if (!article) return;
            const top =
                target.getBoundingClientRect().top -
                article.getBoundingClientRect().top +
                article.scrollTop;
            article.scrollTo({top, behavior});
        },
        []
    );

    useLayoutEffect(() => {
        if (!documentData || isDocumentFetching) return;
        if (
            prevParamsRef.current.resourceId === resourceId &&
            prevParamsRef.current.segmentId === activeSegmentId
        )
            return;

        prevParamsRef.current = {resourceId, segmentId: activeSegmentId};

        const hash = isEmbedded
            ? pendingSectionRef.current
            : location.hash?.slice(1);
        setClickedTocId(hash ?? null);
        if (hash) {
            // Exact id first; if none, a unique case-insensitive match inside
            // the article (related-source anchors arrive as "P3-5" for "p3-5").
            if (isEmbedded) {
                const target = articleRef.current
                    ? findAnchorTarget(hash, articleRef.current)
                    : null;
                if (target) scrollArticleTo(target, "instant");
            } else {
                findAnchorTarget(hash, document, articleRef.current)?.scrollIntoView({
                    behavior: "instant",
                });
            }
            pendingSectionRef.current = undefined;
        } else {
            articleRef.current?.scrollTo({top: 0, behavior: "instant"});
        }
    }, [
        documentData,
        isDocumentFetching,
        resourceId,
        activeSegmentId,
        location.hash,
        isEmbedded,
        scrollArticleTo,
    ]);

    // Right sidebar collapse/expand
    const isDesktop = useMediaQuery("(min-width: 1080px)", undefined, {
        getInitialValueInEffect: false,
    });
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
        () => !window.matchMedia("(min-width: 1080px)").matches
    );

    useEffect(() => {
        if (isDesktop === false) {
            setIsSidebarCollapsed(true);
        }
    }, [isDesktop]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarCollapsed((prev) => !prev);
    }, []);

    // Clear click override on user scroll
    useEffect(() => {
        const clear = () => setClickedTocId(null);
        const inArticle = (e: Event) =>
            articleRef.current?.contains(e.target as Node);
        const handleWheel = (e: WheelEvent) => {
            if (inArticle(e)) clear();
        };
        const handleTouch = (e: TouchEvent) => {
            if (inArticle(e)) clear();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (
                inArticle(e) &&
                [
                    " ",
                    "ArrowUp",
                    "ArrowDown",
                    "PageUp",
                    "PageDown",
                    "Home",
                    "End",
                ].includes(e.key)
            )
                clear();
        };
        document.addEventListener("wheel", handleWheel, {passive: true});
        document.addEventListener("touchstart", handleTouch, {passive: true});
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("wheel", handleWheel);
            document.removeEventListener("touchstart", handleTouch);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const scrollToTop = useCallback((e: React.MouseEvent) => {
        e.currentTarget
            .closest("article")
            ?.scrollTo({top: 0, behavior: "smooth"});
    }, []);

    const navigateEmbeddedToc = useCallback((entry: TocEntry) => {
        const sectionId = entry.id;
        if (entry.seg === "1" && entry.id !== activeSegmentId) {
            pendingSectionRef.current = sectionId;
            setEmbeddedLocation((current) => ({
                ...current,
                resourceId,
                sourceSegmentId: segmentId,
                activeSegmentId: entry.id,
            }));
        } else {
            const target = articleRef.current?.querySelector<HTMLElement>(
                `#${CSS.escape(sectionId)}`
            );
            if (target) scrollArticleTo(target, "smooth");
        }
        onEmbeddedNavigate?.({
            segmentId: entry.seg === "1" ? entry.id : activeSegmentId,
            sectionId,
        });
    }, [
        activeSegmentId,
        onEmbeddedNavigate,
        resourceId,
        scrollArticleTo,
        segmentId,
    ]);

    const [compactTocOpen, setCompactTocOpen] = useState(false);
    const compactTocCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Only the rail and the open list react to the pointer, not the whole gutter column.
    const openCompactToc = useCallback(() => {
        if (compactTocCloseTimer.current) {
            clearTimeout(compactTocCloseTimer.current);
            compactTocCloseTimer.current = null;
        }
        setCompactTocOpen(true);
    }, []);
    const closeCompactToc = useCallback(() => {
        if (compactTocCloseTimer.current) {
            clearTimeout(compactTocCloseTimer.current);
            compactTocCloseTimer.current = null;
        }
        setCompactTocOpen(false);
    }, []);
    // A short grace lets the pointer cross from the rail into the list.
    const scheduleCompactTocClose = useCallback(() => {
        if (compactTocCloseTimer.current) clearTimeout(compactTocCloseTimer.current);
        compactTocCloseTimer.current = setTimeout(() => {
            compactTocCloseTimer.current = null;
            setCompactTocOpen(false);
        }, 120);
    }, []);
    useEffect(() => () => {
        if (compactTocCloseTimer.current) clearTimeout(compactTocCloseTimer.current);
    }, []);
    useEffect(() => {
        if (!compactTocOpen) return;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setCompactTocOpen(false);
        };
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [compactTocOpen]);

    const getArticleContent = () => {
        if (isDocumentFetching && !documentData) {
            return <DocumentSkeleton />;
        }

        if (isError) {
            return <ResourceError error={error} />;
        }

        if (!documentData) {
            return (
                <StatusMessage
                    title="Empty document"
                    detail="This document has no content to display."
                />
            );
        }

        if (documentData.root.length === 0) {
            return (
                <StatusMessage
                    title="Empty document"
                    detail="This document has no content to display."
                />
            );
        }

        const hasDescription = documentData.descroot?.length > 0;

        return (
            <>
                {/* The island header already shows the title. */}
                {!isEmbedded && (
                    <h1 className={classes.title}>
                        {documentData.name}
                        <button
                            type="button"
                            className={classes.scrollToTop}
                            onClick={scrollToTop}
                            aria-label="Scroll to top"
                        >
                            <IconArrowBarToUp />
                        </button>
                    </h1>
                )}
                {hasDescription && (
                    <div className={classes.description}>
                        {renderDescriptionContent(documentData)}
                    </div>
                )}
                <div className={classes.content}>
                    {renderMainContent(documentData)}
                </div>
            </>
        );
    };

    const toc = showToc && tocDocument ? (
        <TableOfContents
            key={activeSegmentId}
            document={tocDocument}
            activeSegmentId={isDocumentFetching ? activeSegmentId : undefined}
            clickedId={isEmbedded ? embeddedCurrentId ?? null : clickedTocId}
            onClickId={setClickedTocId}
            onNavigate={isEmbedded ? navigateEmbeddedToc : undefined}
        />
    ) : null;

    if (embedded) {
        return (
            <div className={`${embeddedClasses.page} ${embedded.compactToc ? embeddedClasses.compact : ""}`}>
                {toc && (
                    <div
                        className={embeddedClasses.tocControl}
                        onFocus={() => openCompactToc()}
                        onBlur={(event) => {
                            if (!event.currentTarget.contains(event.relatedTarget)) closeCompactToc();
                        }}
                    >
                        <div
                            className={embeddedClasses.railWrap}
                            role="button"
                            tabIndex={0}
                            aria-label={t("Title.LabelOfContents", {defaultValue: "Table of contents"})}
                            aria-expanded={compactTocOpen}
                            onPointerEnter={openCompactToc}
                            onPointerLeave={scheduleCompactTocClose}
                        >
                            <TocTickRail
                                entries={flatTocEntries}
                                currentId={embeddedCurrentId}
                                hidden={compactTocOpen}
                            />
                        </div>
                        <div
                            className={`${embeddedClasses.sidebar} ${compactTocOpen ? embeddedClasses.sidebarOpen : ""}`}
                            onPointerEnter={openCompactToc}
                            onPointerLeave={scheduleCompactTocClose}
                        >
                            {toc}
                        </div>
                    </div>
                )}
                <article ref={articleRef} className={embeddedClasses.article}>
                    <div className={embeddedClasses.articleInner}>{getArticleContent()}</div>
                </article>
            </div>
        );
    }

    return (
        <div className={layoutClasses.page}>
            <nav className={layoutClasses.sidebar}>
                {toc}
            </nav>

            <article ref={articleRef} className={layoutClasses.article}>
                <div className={layoutClasses.articleInner}>
                    {getArticleContent()}
                </div>
            </article>

            <aside
                className={`${layoutClasses.onThisPage} ${isSidebarCollapsed ? layoutClasses.onThisPageCollapsed : ""} ${!isDesktop ? layoutClasses.onThisPageMobile : ""}`}
                style={
                    isSidebarCollapsed
                        ? {width: 0, flexBasis: 0}
                        : undefined
                }
            >
                <Tooltip
                    label={
                        isSidebarCollapsed
                            ? t("common.expandSidebar")
                            : t("common.collapseSidebar")
                    }
                    position="left"
                >
                    <ActionIcon
                        className={layoutClasses.sidebarToggle}
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={toggleSidebar}
                    >
                        {isSidebarCollapsed ? (
                            <IconArrowLeft />
                        ) : (
                            <IconArrowRight />
                        )}
                    </ActionIcon>
                </Tooltip>
                <Box className={layoutClasses.onThisPageContent}>
                    {(!isDocumentFetching || isIntraTopicFetch) && (
                        <>
                            <SupplierInfo
                                resourceId={resourceId}
                                segmentId={segmentId}
                            />
                            <RelatedContent
                                resourceId={resourceId}
                                segmentId={segmentId}
                            />
                        </>
                    )}
                </Box>
            </aside>
        </div>
    );
};
